import React from 'react';
import videojs from 'video.js';
import vttThumbnails from 'videojs-vtt-thumbnails/src/plugin.js';

import { UserContext } from '../../context/index.js';

import 'videojs-hotkeys';
import classnames from 'classnames';
import Loader from 'react-simple-loading';

// utils
import callApi from '../../utils/callApi';

// constants
import {
  FEATURED_MAIN_PROGRAM,
  FEATURED_MAIN_PROGRAM_GUEST,
  FETCH_VIDEO_SEGMENT_BY_ID,
  FETCH_VIDEO_PROGRAM_BY_ID,
  FETCH_VIDEO_VINTAGE_BY_ID,
  FETCH_VIDEO_AUDITION_BY_ID,
  TRACK_SEGMENT_VIDEO_WATCH,
  FREEMIUM_USER_FEATURED_SEGMENT,
} from '../../constants/apiUrls/apiUrls';

// components
import VideoPlayerStore from '../../store/VideoPlayerStore.js';

import PlayerInfo from './PlayerInfo';
import '../../css/player.css';
import RelatedSegmentsListVertical from './RelatedSegmentsVertical';
import CastVideoPlayer from './CastVideoPlayer';
import './CastVideoPlayer.css';


const DEFAULT_VIDEO_RESOLUTION = 'SIZE_1080';


class VideoPlayer extends React.Component {
  constructor(props) {
    super(props);
    const { user } = props;
    const useHLSAbr = (user && user.features && (user.features.indexOf('HLS_ABR') >= 0));

    this.state = {
      isLoggedIn: false,
      showSubscriptionModal: false,
      resolution: useHLSAbr ? 'Auto' : DEFAULT_VIDEO_RESOLUTION,
      defaultClip: null,
    };

    this.seekTimeAtFailed = null;
    this.intervalId = null;
    this.monitorId = null;
    this.lastPlayed = null;
    this.queryParams = new URLSearchParams(window.location.search);
    this.affiliatePartnerCode = this.queryParams?.get('affiliatePartnerCode');
  }

  monitor = () => {
    const { currentPosition } = VideoPlayerStore;
    const now = new Date().getTime();
    this.sendWatchTracking(currentPosition, now - this.lastPlayed);
    this.lastPlayed = now;
  };

  playEvent = () => {
    this.lastPlayed = new Date().getTime();
    this.monitorId = setInterval(this.monitor, 10000);
  };

  pauseEvent = () => {
    const { currentPosition } = VideoPlayerStore;
    this.pause(currentPosition);
  };

  pause = (position) => {
    if (this.lastPlayed !== null) {
      const now = new Date().getTime();
      this.sendWatchTracking(position, now - this.lastPlayed);
      clearInterval(this.monitorId);
      this.lastPlayed = null;
    }
  };

  sendWatchTracking = async(position, msecs) => {
    if (msecs > 0) {
      const { programSegments } = VideoPlayerStore;
      const { programSegmentId } = programSegments[position];
      if (programSegmentId) {
        const url = TRACK_SEGMENT_VIDEO_WATCH.
          replace('{programSegmentId}', programSegmentId);
        const data = {
          duration: ~~(this.player.duration() * 1000),
          msecs,
        };
        callApi(url, 'PATCH', false, data, true, true).
          catch((err) => console.log(err));
      }
    }
  };

  openSubscriptionModal = () => {
    this.setState({ showSubscriptionModal: true });
  };

  closeSubscriptionModal = () => {
    this.setState({ showSubscriptionModal: false });
  };

  playButton = () => {
    if (this.player) {
      this.player.play().catch((e) => null);
    }
  };

  applyResolution = (resolutionSize) => {
    const { program, currentPosition, programSegments } = VideoPlayerStore;

    const clip = program?.programSegments?.[currentPosition]?.clip || programSegments?.[currentPosition]?.clip;
    const currentTime = this.player.currentTime();
    const srcUrl = this.getVideoSrc(clip, resolutionSize);

    this.setState({ resolution: resolutionSize });
    this.player.poster(null);
    this.player.src(srcUrl);
    this.player.play().then(() => {
      this.player.currentTime(currentTime);
    });
  };

  changeVideoSrc = (programSegment, position) => {
    const { currentPosition, program, lastPlayedProgramId } = VideoPlayerStore;
    const { resolution } = this.state;
    const { user } = this.props;
    const hasProgramChanged = (program?.id && (lastPlayedProgramId !== programSegment?.programSegmentId));
    if (!this.player) {
      return false;
    }
    if (!user.authenticated && position !== 0) {
      this.openSubscriptionModal();
    } else {
      if (typeof programSegment === 'undefined') {
        return false;
      }
      const error = this.player.error();
      if (currentPosition !== position) {
        this.seekTimeAtFailed = null;
      }
      if (hasProgramChanged || (currentPosition !== position) ||
                error ||
                this.props.guestVideoPlayer) {
        this.setState({ currentPosition }, () => {
          VideoPlayerStore.setCurrentPosition(position);
          this.pause(currentPosition);
          this.player.src(this.getVideoSrc(programSegment.clip, resolution));
          // remove next segment poster
          this.player.poster(null);
          // reset download items
          if (program?.programSegments) {
            const DownloadMenu = this.player.getChild('controlBar').getChild('DownloadMenu');
            // this is required to keep download button from vintage videos ;(
            if (DownloadMenu) {
              this.player.getChild('controlBar').removeChild('DownloadMenu');
              this.player.getChild('controlBar').addChild(
                'DownloadMenu',
                { items: this.getProgramItems() },
                this.player.controlBar.children_.length
              );
            }
          }
          this.player.play().catch((e) => null);
          this.setVTT(programSegment.clip);
        });
      }
    }
  };

  createDownloadButton = () => {
    const MenuItem = videojs.getComponent('MenuItem');
    const DownloadItem = videojs.extend(MenuItem, {
      constructor(player, options) {
        this.level = options.label;
        this.href = options.href;
        MenuItem.call(this, player, options);
        if (!this.href) {
          videojs.dom.addClass(this.el(), 'vjs-download-title');
        }
        this.controlText(this.level);
      },
    });
    DownloadItem.prototype.handleClick = function(event) {
      MenuItem.prototype.handleClick.call(this, event);
      if (this.href) {
        if (window.gtag) {
          window.gtag('event', 'download', {
            event_category: 'Video',
            event_label: this.level,
            value: this.href,
          });
        }
        window.open(this.href, '_blank');
      }
    };
    MenuItem.registerComponent('DownloadItem', DownloadItem);
    const MenuButton = videojs.getComponent('MenuButton');
    const DownloadMenu = videojs.extend(MenuButton, {
      constructor(player, options) {
        this.items = options.items;
        MenuButton.call(this, player, options);
        this.controlText('Download');
        this.el().setAttribute('aria-label', 'Download Menu');
        videojs.dom.addClass(this.el(), 'vjs-download-button');
      },
      createItems() {
        let i; let menuItems; let ref; let value;
        menuItems = [];
        ref = this.items;
        for (i in ref) {
          value = ref[i];
          menuItems.push(new DownloadItem(this.player_, value));
        }

        return menuItems;
      },
      dispose() {
        return this.parentNode && this.parentNode.removeChild(this);
      },
    });
    MenuButton.registerComponent('DownloadMenu', DownloadMenu);
  };

  createPlayNextButtonForVideoJs = () => {
    const self = this;
    const Button = videojs.getComponent('Button');
    const nextButton = videojs.extend(Button, {
      constructor() {
        Button.apply(this, arguments);
        this.addClass('icon-angle-right');
      },
      handleClick() {
        const { currentPosition, setCurrentPosition, programSegments } = VideoPlayerStore;
        self.changeVideoSrc(
          programSegments[
            currentPosition + 1
          ],
          currentPosition + 1
        );
        setCurrentPosition(currentPosition + 1);
      },
    });

    videojs.registerComponent('nextButton', nextButton);
  };

  createResolutionMenu = () => {
    const self = this;
    const MenuItem = videojs.getComponent('MenuItem');
    const ResolutionItem = videojs.extend(MenuItem, {
      constructor(player, options) {
        this.level = options.label;
        MenuItem.call(this, player, options);
        this.controlText(this.level);
        if (options.size === self.state.resolution) {
          videojs.dom.addClass(this.el(), 'vjs-selected');
        }
      },
    });
    ResolutionItem.prototype.handleClick = function(event) {
      MenuItem.prototype.handleClick.call(this, event);
      const options = this.options();
      const siblings = this.el().parentNode.children;

      if (options.resolution === 'Auto') {
        self.applyResolution(DEFAULT_VIDEO_RESOLUTION);
      } else {
        self.applyResolution(options.size);
      }
      for (let i = 0; i < siblings.length; i++) {
        videojs.dom.removeClass(siblings[i], 'vjs-selected');
      }
      videojs.dom.addClass(this.el(), 'vjs-selected');
    };
    MenuItem.registerComponent('ResolutionItem', ResolutionItem);
    const MenuButton = videojs.getComponent('MenuButton');
    const ResolutionMenu = videojs.extend(MenuButton, {
      constructor(player, options) {
        this.items = options.items;
        MenuButton.call(this, player, options);
        this.controlText('Download');
        this.el().setAttribute('aria-label', 'Resolution Menu');
        videojs.dom.addClass(this.el(), 'vjs-icon-hd');
      },
      createItems() {
        let i; let menuItems; let ref; let value;
        menuItems = [];
        ref = this.items;
        for (i in ref) {
          value = ref[i];
          menuItems.push(new ResolutionItem(this.player_, value));
        }

        return menuItems;
      },
      dispose() {
        return this.parentNode && this.parentNode.removeChild(this);
      },
    });
    MenuButton.registerComponent('ResolutionMenu', ResolutionMenu);
  };

  createPlayPreviousButtonForVideoJs = () => {
    const self = this;
    const Button = videojs.getComponent('Button');
    const prevButton = videojs.extend(Button, {
      constructor() {
        Button.apply(this, arguments);
        this.addClass('icon-angle-left');
      },
      handleClick() {
        const { currentPosition, setCurrentPosition, programSegments } = VideoPlayerStore;
        self.changeVideoSrc(
          programSegments[
            currentPosition - 1
          ],
          currentPosition - 1
        );
        setCurrentPosition(currentPosition - 1);
      },
    });

    videojs.registerComponent('prevButton', prevButton);
  };

  skip = (timeToSkip) => {
    const currentTime = this.player.currentTime();
    this.player.currentTime(currentTime + timeToSkip);
  };

  createSkipButtonForVideoJs = (className, skipTime, buttonName) => {
    const self = this;
    const Button = videojs.getComponent('Button');
    const skipButton = videojs.extend(Button, {
      constructor() {
        Button.apply(this, arguments);
        this.addClass(className);
      },
      handleClick() {
        self.skip(skipTime);
      },
    });

    videojs.registerComponent(buttonName, skipButton);
  };

  decideUrl = () => {
    const { videoElement, typeOfListing, user } = this.props;
    const isSubscriptionExpired = user.authenticated && !user.user.activeSubscription;
    if (typeof videoElement !== 'undefined') {
      switch (typeOfListing) {
        case 'all':
        case 'date':
          if (videoElement.programId) {
            return FETCH_VIDEO_PROGRAM_BY_ID.replace(
              '{id}',
              videoElement.programId
            );
          }
          if (videoElement.programSegmentId) {
            return FETCH_VIDEO_SEGMENT_BY_ID.replace(
              '{id}',
              videoElement.programSegmentId
            );
          }
        case 'audition':
          return FETCH_VIDEO_AUDITION_BY_ID.replace(
            '{id}',
            videoElement.auditionId
          );
        case 'vintage':
          return FETCH_VIDEO_VINTAGE_BY_ID.replace(
            '{id}',
            videoElement.id
          );
        default:
          return FETCH_VIDEO_SEGMENT_BY_ID.replace(
            '{id}',
            videoElement.programSegmentId
          );
      }
    }
    if ((user.authenticated && (!isSubscriptionExpired || !!user.user.account.anchor))) {
      return FEATURED_MAIN_PROGRAM;
    }
    if (user.authenticated && user?.user?.account?.freemiumUser && !user?.user?.activeSubscription) {
      return FREEMIUM_USER_FEATURED_SEGMENT;
    }

    return FEATURED_MAIN_PROGRAM_GUEST;
  };

  getProgramSegment = (programSegment) => programSegment.programSegmentId ? {
    programSegmentId: programSegment.programSegmentId,
    segment: programSegment.segment,
    description: programSegment.description,
    createdAt: programSegment.createdAt,
    clip: programSegment.clip,
    freemium: programSegment.freemium,
    slug: programSegment?.slug,
    tags: programSegment?.tags,
  } : {
    programSegmentId: undefined,
    segment: { name: '' },
    description: '',
    createdAt: undefined,
    clip: undefined,
    freemium: false,
    slug: undefined,
    tags: [],
  };


  getQueryParams = () => {
    const queryParams = {};
    if (this.affiliatePartnerCode?.length) {
      queryParams.affiliatePartnerCode = this.affiliatePartnerCode;
    }

    return queryParams;
  };

  fetchMainProgram = (url) => {
    const { searchAnchorId, searchSegmentId, user } = this.props;
    callApi(url, 'get', this.getQueryParams()).then((res) => {
      let currentPosition = 0;
      const program = res.data;
      this.setState({ defaultClip: res?.data?.clip?.defaultClipUrl });
      let programSegments = (program?.programSegments || [program]).map(this.getProgramSegment);
      if (user?.user?.activeSubscription && programSegments.length > 1) {
        programSegments = programSegments.filter((segment) => !segment.freemium);
      }
      if (!user?.user?.activeSubscription &&
                user?.user?.account.freemiumUser &&
                (this.props.guestHomePage || this.props.guestVideoPlayer)) {
        currentPosition = programSegments.findIndex((segment) => segment.freemium);
      }
      if (searchAnchorId) {
        currentPosition = program?.programSegments ? programSegments.reduce((acc, seg, ind) => {
          if (seg.anchors && seg.anchors.length) {
            seg.anchors.forEach((anc) => {
              if (anc.id == searchAnchorId) {
                acc = ind;
              }
            });
          }

          return acc;
        }, 0) : 0;
      }
      if (searchSegmentId) {
        currentPosition = program.programSegments ? programSegments.reduce((acc, seg, ind) => {
          if (seg.segment.id == searchSegmentId) {
            acc = ind;
          }

          return acc;
        }, 0) : 0;
      }

      VideoPlayerStore.setProgramData({ program, programSegments, currentPosition });
      this.setState({ program, programSegments, currentPosition }, () =>
        this.initVideoJs()
      );
    });
  };

  setVTT = (clip) => {
    if (this.player.vttThumbnails && clip && clip.vtt) {
      if (typeof this.player.vttThumbnails === 'function') {
        // first use
        this.player.vttThumbnails({
          src: clip.vtt.SRC,
          sprite: clip.vtt.SPRITE,
        });
      } else {
        // update src
        this.player.vttThumbnails.src(clip.vtt.SRC, clip.vtt.SPRITE);
      }
      const img1 = document.createElement('img');
      img1.src = clip.vtt.SPRITE; // Assigning the img src immediately requests the image
    }
  };

  getProgramItems = () => {
    const { program, currentPosition, programSegments } = VideoPlayerStore;
    const segments = program.programSegments || programSegments;
    const { downloadClipUrls } = segments[currentPosition]?.clip || {};
    const items = [{ label: 'Program' }];

    if (program.fullVideoUrls && program.isHd) {
      program.fullVideoUrls.SIZE_1080 &&
                items.push({ label: '1080p', href: program.fullVideoUrls.SIZE_1080 });
      program.fullVideoUrls.SIZE_720 &&
                items.push({ label: '720p', href: program.fullVideoUrls.SIZE_720 });
      program.fullVideoUrls.SIZE_480 &&
                items.push({ label: '480p', href: program.fullVideoUrls.SIZE_480 });
      program.fullVideoUrls.SIZE_360 &&
                items.push({ label: '360p', href: program.fullVideoUrls.SIZE_360 });
    }

    if (downloadClipUrls) {
      items.push({ label: 'Segment' });
      downloadClipUrls.SIZE_1080 &&
                items.push({
                  label: '1080p',
                  href: downloadClipUrls.SIZE_1080,
                });
      downloadClipUrls.SIZE_720 &&
                items.push({ label: '720p', href: downloadClipUrls.SIZE_720 });
      downloadClipUrls.SIZE_480 &&
                items.push({ label: '480p', href: downloadClipUrls.SIZE_480 });
      downloadClipUrls.SIZE_360 &&
                items.push({ label: '360p', href: downloadClipUrls.SIZE_360 });
    }

    return items;
  };

  getSegmentItems = () => {
    const { program } = VideoPlayerStore;
    const { downloadClipUrls } = program.clip;
    const items = [];

    downloadClipUrls.SIZE_1080 &&
            items.push({ label: '1080p', href: downloadClipUrls.SIZE_1080 });
    downloadClipUrls.SIZE_720 &&
            items.push({ label: '720p', href: downloadClipUrls.SIZE_720 });
    downloadClipUrls.SIZE_480 &&
            items.push({ label: '480p', href: downloadClipUrls.SIZE_480 });
    downloadClipUrls.SIZE_360 &&
            items.push({ label: '360p', href: downloadClipUrls.SIZE_360 });

    return items;
  };

  getVideoResolutions = () => {
    const { program, programSegments, currentPosition } = VideoPlayerStore;
    const standardUrls = Object.values(program.clip).every((v) => v !== null) ? program.clip.clipUrls : programSegments[currentPosition]?.clip?.clipUrls;
    const items = Object.keys(standardUrls).map((size) => {
      const resolutionBase = size.replace('SIZE_', '');

      return {
        size,
        resolution: resolutionBase,
        label: `${resolutionBase}p`,
      };
    }).sort((a, b) => b.resolution - a.resolution);
    items.push({ resolution: 'Auto', label: 'Auto', size: 'Auto' });

    return items;
  };

  getVideoSrc = (clip, resolution) => {
    const { user } = this.props;
    const { programSegments } = VideoPlayerStore;

    resolution = resolution || DEFAULT_VIDEO_RESOLUTION;
    const useHLS = (user && user.features && (user.features.indexOf('HLS') >= 0));
    const useHLSOverCdn = (user && user.features && (user.features.indexOf('HLS_OVER_CDN') >= 0));
    const useHLSAbr = (user && user.features && (user.features.indexOf('HLS_ABR') >= 0));

    let srcUrl;
    if ((!user || !user.authenticated) && this.props.homePage) {
      srcUrl = this.state.defaultClip;
    } else if (resolution === 'Auto' && clip.abrClipUrl) {
      console.log('Using HLS ABR');
      srcUrl = clip.abrClipUrl;
    } else if (useHLSOverCdn && clip?.clipHlsCdnUrls) {
      console.log(`Using HLS over CDN with resolution: ${resolution}`);
      srcUrl = clip?.clipHlsCdnUrls[resolution];
    } else if (useHLS) {
      console.log(`Using HLS with resolution: ${resolution}`);
      srcUrl = clip?.clipHlsUrls ? clip?.clipHlsUrls?.[resolution] : (clip?.defaultClipUrl);
    } else {
      // in case of older videos not having abr stream
      if (useHLSAbr && !clip.abrClipUrl) {
        srcUrl = clip.clipUrls[DEFAULT_VIDEO_RESOLUTION];
        this.setState({ resolution: DEFAULT_VIDEO_RESOLUTION });
      } else {
        srcUrl = clip?.clipUrls ? clip?.clipUrls?.[resolution] : (clip?.defaultClipUrl);
      }
    }
    if (programSegments.length < 2 && user?.user?.account?.freemiumUser && (this.props.guestHomePage || this.props.guestVideoPlayer)) {
      return this.state.program?.defaultClipUrl;
    }

    return srcUrl || this.state.defaultClip;
  };

  hasHLSSources = ((clip) => {
    const { user } = this.props;
    const useHLS = (user && user.features && (user.features.indexOf('HLS') >= 0));
    const useHLSOverCdn = (user && user.features && (user.features.indexOf('HLS_OVER_CDN') >= 0));
    const hasHLSActive = useHLS || useHLSOverCdn;
    const hasHLSURLS = clip?.clipHlsUrls || clip?.clipHlsCdnUrls;
    const res = Boolean(hasHLSActive && hasHLSURLS);

    return res;
  });

  componentDidUpdate(prevProps) {
    if (this.props.guestVideoPlayer) {
      if (prevProps?.selectedVideo.id !== this.props?.selectedVideo.id) {
        this.changeVideoSrc(this.props.selectedVideo?.programSegments?.[0], 0);
        VideoPlayerStore.setProgramData({
          program: this.props.selectedVideo,
          programSegments: this.props.selectedVideo.programSegments,
        });
      }
    }
  }

  handleVideoEnd = () => {
    const { currentPosition: videoPosition, programSegments: segments, program: programCurrent } = VideoPlayerStore;
    this.player.poster(null);
    const error = this.player.error();
    VideoPlayerStore.setLastPlayedProgramId(programCurrent?.programSegments?.[videoPosition]?.programSegmentId);
    if (error) {
      this.changeVideoSrc(segments[videoPosition], videoPosition);
    } else if (!this.props.guestVideoPlayer && !this.props.guestHomePage) {
      VideoPlayerStore.setCurrentPosition(videoPosition + 1);
      this.changeVideoSrc(segments[videoPosition + 1], videoPosition + 1);
    }
  };

  handlePlayerError = () => {
    const mediaError = this.player.error();
    const time = this.player.currentTime();
    this.player.errorDisplay.close();
    if (mediaError.code === 2) {
      this.seekTimeAtFailed = time;
      if (!this.intervalId) {
        this.intervalId = setInterval(this.playerReload, 1000);
      }
    }
    if (mediaError.code === 3) {
      this.seekTimeAtFailed = time;
      if (!this.intervalId) {
        this.intervalId = setInterval(this.playerReload, 5000);
      }
    }
    if (mediaError.code === 4) {
      this.seekTimeAtFailed = time;
      if (!this.intervalId) {
        this.intervalId = setInterval(this.playerReload, 5000);
      }
    }

  };

  initVideoJs = () => {
    const { program, programSegments, currentPosition } = VideoPlayerStore;
    const { resolution } = this.state;
    const { user, guestHomePage, guestVideoPlayer } = this.props;
    const useHLS = (user && user.features && (user.features.indexOf('HLS') >= 0));
    const useHLSOverCdn = (user && user.features && (user.features.indexOf('HLS_OVER_CDN') >= 0));
    const useHLSAbr = (user && user.features && (user.features.indexOf('HLS_ABR') >= 0));
    const userCanDownload = user?.user?.account?.hasDownloadMenu && programSegments?.[currentPosition]?.clip?.downloadClipUrls;
    if (program === undefined) {
      this.player && this.player.src('');

      return;
    }
    videojs.registerPlugin('vttThumbnails', vttThumbnails);
    if (userCanDownload && (user.user.activeSubscription || user.user.account.anchor)) {
      this.createDownloadButton();
    }

    const self = this;
    // if it has segments then  its a PROGRAM , if not its a SEGMENT
    if (program) {
      this.createSkipButtonForVideoJs('forward-10', 10, 'forwardButton');
      this.createSkipButtonForVideoJs('backward-10', -10, 'backwardButton');
      if (program.programSegments) {
        this.createPlayNextButtonForVideoJs();
        this.createPlayPreviousButtonForVideoJs();
      }
      if ((useHLSAbr || useHLS || useHLSOverCdn) && self.hasHLSSources(programSegments?.[currentPosition]?.clip)) {
        this.createResolutionMenu();
      }
      // instantiate Video.js
      this.player = videojs(
        this.videoNode,
        { ...this.props, playbackRates: [0.5, 1, 1.5, 2] },
        function onPlayerReady() {
          if (!guestHomePage && !guestVideoPlayer) {
            this.hotkeys({
              volumeStep: 0.1,
              seekStep: 10,
              captureDocumentHotkeys: false,
              enableVolumeScroll: false,
              enableHoverScroll: false,
            });
          }
          // Removing default volume panel and adding the vertical volume panel
          this.getChild('controlBar').removeChild('volumePanel');
          this.getChild('controlBar').addChild('volumePanel', { inline: false, vertical: true }, 1);
          this.getChild('controlBar').addChild(
            'forwardButton',
            { text: 'Fast Forward' },
            1
          );
          this.getChild('controlBar').addChild(
            'backwardButton',
            { text: 'Rewind' },
            1
          );
          if (program.programSegments) {
            // Adding the left and right buttons in the player element to position it perfectly on mobile
            this.$('.vjs-tech', this.addChild('nextButton', { text: 'Next', inactivityTimeout: 5000 }, 1));
            this.$('.vjs-tech', this.addChild('prevButton', { text: 'Previous', inactivityTimeout: 5000 }, 1));

            this.getChild('controlBar').addChild(
              'nextButton',
              { text: 'Next' },
              1
            );
            this.getChild('controlBar').addChild(
              'prevButton',
              { text: 'Previous' },
              1
            );
          }
          if ((useHLSAbr || useHLS || useHLSOverCdn) && self.hasHLSSources(programSegments?.[currentPosition]?.clip)) {
            this.getChild('controlBar').addChild(
              'ResolutionMenu',
              { items: self.getVideoResolutions() },
              this.controlBar.children_.length
            );
          }
          if (userCanDownload) {
            this.getChild('controlBar').addChild(
              'DownloadMenu',
              { items: self.getProgramItems() },
              this.controlBar.children_.length
            );
          }
          if (program.programSegments) {
            this.src(self.getVideoSrc(programSegments[currentPosition]?.clip), resolution);
          } else {
            this.src(self.getVideoSrc(program.clip, resolution));
          }
          if (self.props.mutedVideo) {
            const videoEl = document.getElementsByTagName('video')[0];
            videoEl.muted = true;
            this.volume(0);
          }
        },
      );
      this.setVTT(programSegments[currentPosition]?.clip);

      this.player.on('error', this.handlePlayerError);
      this.player.on('ended', this.handleVideoEnd);
    } else {
      if (useHLSAbr && self.hasHLSSources(program?.clip)) {
        this.createResolutionMenu();
      }
      this.player = videojs(
        this.videoNode,
        this.props,
        function onPlayerReady() {
          if (program.clip.downloadClipUrls && user.authenticated && userCanDownload) {
            if (useHLSAbr && self.hasHLSSources(program?.clip)) {
              this.getChild('controlBar').addChild(
                'ResolutionMenu',
                { items: self.getVideoResolutions() },
                this.controlBar.children_.length
              );
            }
            if (userCanDownload) {
              this.getChild('controlBar').addChild(
                'DownloadMenu',
                { items: self.getProgramItems() },
                this.controlBar.children_.length
              );
            }
          }
          this.src(self.getVideoSrc(program.clip, resolution));
          if (self.props.mutedVideo) {
            const videoEl = document.getElementsByTagName('video')[0];
            videoEl.muted = true;
            this.volume(0);
          }
        }
      );
      this.player.on('ended', () => {
        if (user.authenticated === false) {
          this.setState({ showSubscriptionModal: true });
        }
      });
      this.setVTT(program.clip);
    }

    this.player.on('pause', this.pauseEvent);
    this.player.on('play', this.playEvent);

    this.player.poster(this.getPoster());
  };

  playerReload = () => {
    const error = this.player.error();
    if (error) {
      // this.changeVideoSrc(
      //   this.state.program.programSegments[
      //     this.state.currentPosition
      //   ],
      //   this.state.currentPosition
      // );
      this.player.currentTime(this.seekTimeAtFailed);
      this.player.play().catch((e) => null);
    } else {
      this.clearReconnectInterval();
    }
  };

  getProgram = () => {
    const url = this.decideUrl();
    if (url) {
      this.fetchMainProgram(url);
    }
  };

  componentDidMount() {
    this.getProgram();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps?.videoElement?.programSegmentId !== this.props?.videoElement?.programSegmentId) {
      this.getProgram();
    }
  }

  // destroy player on unmount
  componentWillUnmount() {
    this.pauseEvent();
    if (this.player) {
      this.player.dispose();
    }
    this.clearReconnectInterval();
  }

  clearReconnectInterval = () => {
    clearInterval(this.intervalId);
    this.intervalId = null;
  };

  decidePlayerInfo = () => {
    const { program, programSegments, currentPosition } = VideoPlayerStore;
    const {
      isProgram,
      user,
      forceHideNextSegmentButton,
      hidePlayerInfo,
    } = this.props;

    if (isProgram && !hidePlayerInfo && user?.user?.activeSubscription) {
      return (
        <PlayerInfo
          changeVideoSrc={this.changeVideoSrc}
          clearReconnectInterval={this.clearReconnectInterval}
          currentPosition={currentPosition}
          forceHideNextSegmentButton={forceHideNextSegmentButton}
          isSubscribed={
            user && user?.activeSubscription
          }
          program={program}
          programSegments={programSegments}
          user={user}
        />
      );
    }

    return false;
  };

  getPoster = (targetPosition) => {
    const { user } = this.props;
    const { program, currentPosition } = VideoPlayerStore;
    if (targetPosition === null) {
      return null;
    }

    const position = targetPosition || currentPosition;
    const poster = program?.programSegments ? program?.programSegments[position]?.clip?.imageUrl : (program.nextImageUrl || program?.clip?.imageUrl);

    if (user?.sfw) {
      return program?.programSegments?.[position]?.sfwAssetImage || poster;
    }

    return poster;
  };

  setVideoIsCasting = (boolean) => {
    this.setState({ videoIsBeingCasted: boolean });
    this.player.pause();
  };

  render() {
    const { videoIsBeingCasted } = this.state;
    const { program, programSegments, currentPosition } = VideoPlayerStore;
    const {
      customBcgColor,
      autoPlayVideo,
      loopVideo,
      mutedVideo,
      user,
      guestHomePage,
      hideRelatedSegments,
      removePadding,
      takeFullWidth,
    } = this.props;

    const videoClass = 'video-js vjs-sk vjs-default-skin vjs-paused vp_18_video-dimensions vjs-fluid vjs-controls-enabled vjs-workinghover vjs-v6 vjs-user-active';

    return (

      <div
        className={
          classnames('masthead', {
            'guest-video-player': guestHomePage,
            'padding-0': removePadding,
          })
        }
        style={{ backgroundColor: customBcgColor ? customBcgColor : '#252525' }}
      >
        <div className={classnames({ 'container': !takeFullWidth, 'fw-container': takeFullWidth })}>
          {!hideRelatedSegments && <h3 className="now-playing-title">NOW PLAYING</h3>}
          <div
            className="clearfix video-player-container preview video-player-container-mobile"
            id="video-player-container"
          >
            <div className="full-player">
              <div className="player-zone">
                <div className="video-player">
                  <div
                    className={
                      classnames(
                        'mobile-video-wrapper',
                        {
                          'cast-is-active': videoIsBeingCasted,
                          'sk-video-player-without-segment': programSegments?.length < 2 || hideRelatedSegments,
                        })}
                    id="vp_18"
                  >
                    <div
                      className={
                        `sk-video-player ${programSegments?.programSegmentId === undefined ? 'sk-video-player-full' : ''} ${programSegments?.length < 2 ? 'full-width' : ''}`}
                      id="vp_18_wrapper"
                    >
                      {!program && <Loader color="#fff" />}
                      <video
                        ref={(node) =>
                          (this.videoNode = node)
                        }
                        autoPlay={autoPlayVideo}
                        className={videoClass}
                        controls={true}
                        id="vp_18_video_html5_api"
                        loop={loopVideo}
                        muted={mutedVideo}
                        playinline=""
                        playsInline={true}
                        preload="none"
                        tabIndex="-1"
                        type="application/x-mpegURL"
                      />
                      {window.chrome && <CastVideoPlayer getVideoSrc={this.getVideoSrc} segments={programSegments} setVideoIsCasting={this.setVideoIsCasting} user={user} />}
                    </div>
                    {(hideRelatedSegments || programSegments?.length === 1 || guestHomePage) ? null : <RelatedSegmentsListVertical
                      changeVideoSrc={this.changeVideoSrc}
                      currentPosition={currentPosition}
                      isPromoActive={this.props.isPromoActive}
                      openSubscriptionModal={this.openSubscriptionModal}
                      program={program}
                      programSegments={programSegments}
                      user={user}
                                                                                                      />
                    }
                    {this.decidePlayerInfo()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

VideoPlayer.contextType = UserContext;

export default VideoPlayer;

VideoPlayer.defaultProps = {
  guestHomePage: false,
  guestVideoPlayer: false,
  hideRelatedSegments: false,
  removePadding: false,
  takeFullWidth: false,
};
